import React, { Component } from "react";
import { v4 as uuid } from "uuid";
import { intlShape, injectIntl } from "@ai360/core/intl";
import { Ai360API, ReportAPI, PicklistAPI, MessageDescriptor } from "@ai360/core";
import { connect, ConnectedComponent } from "react-redux";
import Immutable from "immutable";

import {
    actions as picklistActions,
    picklistNames,
    selectors as picklistSelectors,
} from "~/core/picklist";
import { actions as vendorActions } from "~/admin/setup/vendor/data";
import { actions as unitActions, unitNames } from "~/core/units";
import { withMasked } from "~/hocs";
import { actions, constants, model, selectors } from "../data";
import { getFieldGuidArray } from "../utils";
import { messages } from "../i18n-messages";
import { DialogBox, DialogBoxFooterType } from "~/core";
import { keywords } from "../keywords";
import { loadsheetPreventionStatusCodes } from "~/action-panel/components/common/status-codes";
import { errorCodeMessages } from "~/i18n-error-messages";
import {
    ICropYieldData,
    IFileNameFormat,
    IMessageOptions,
    IReportTypePage,
    IReportType,
} from "../data/interfaces";
import { RecSummary } from "~/recs-events/recs/model";
import WithReportErrors from "./with-report-errors";
import reportOptionsComponents from "~/reports/reports";
import { NEW_REPORTS } from "../data/constants";
import { getSelectedNonFieldFeaturesGuids, useReportCropScope } from "~/reports/data/selectors";
import { selectors as cdSelectors } from "~/customer-data";
import { getSelectedEventGuidSet } from "~/action-panel/components/event-module/components/event-list/selectors";
import { getSelectedRecGuidSet } from "~/action-panel/components/rec-module/components/rec-list/selectors";
import { recsSelectors } from "~/recs-events";
import { getSelectedCustomerGuids } from "~/customer-data/selectors";
import { FAILURE, SUCCESS } from "~/hocs/needs/utils";
import { IYieldByCategory } from "~/reports/data/model";
import {
    ICropDropdownOption,
    isCropUnlinked,
    toCropDropdownOptions,
    useCropList,
} from "~/utils/crops/use-crop-list";

interface IErrorMessage {
    message: MessageDescriptor;
    messageOptions?: IMessageOptions;
}

interface IReportStatus extends ReportAPI.IField {
    [model.PROPS_REPORT_STATUS]: string;
}

export interface IReportData {
    [model.PROPS_AGE_OF_SAMPLE]: string;
    [model.PROPS_ALL_CUSTOMERS_FIELDS_YN]: boolean;
    [model.PROPS_CROPPING_SEASON_GUID_LIST]: string[];
    [model.PROPS_BATCH_GUID]: string;
    [model.PROPS_BREAK_EVEN_CROP_DATA]: ICropYieldData[];
    [model.PROPS_CROP_DATA]: ICropYieldData[];
    [model.PROPS_CROP_MAP_ORIENTATION]: string;
    [model.PROPS_CROP_GUID]: string;
    [model.PROPS_END_DATE]: string;
    [model.PROPS_FIELD_ITEM_LIST]: IReportStatus[] | string[];
    [model.PROPS_LAB_ACCOUNT]: string;
    [model.PROPS_MULTI_FIELD_REC]: boolean;
    [model.PROPS_PER_REPORT_OPTION]: string;
    [model.PROPS_REPORT_TYPE_GUID_LIST]: string[];
    [model.PROPS_REPORT_TYPE_PAGE_SELECTION]: string;
    [model.PROPS_START_DATE]: string;
    [model.PROPS_START_POSITION]: number;
    [model.PROPS_SELECTED_BASEMAP]: string;
    [model.PROPS_SELECTED_FILENAME_FORMAT]: string;
    [model.PROPS_SHOW_REC_SUMMARY]: boolean;
    [model.PROPS_SHOW_SCOUTING_DETAILS]: boolean;
    [model.PROPS_SHOW_YIELD_POLYGON]: boolean;
    [model.PROPS_SELECTED_EVENT_GUID_LIST]: string[];
    [model.PROPS_SELECTED_REC_GUID_LIST]: string[];
    [model.PROPS_TESTING_LAB_SAMPLE_TYPE_GUID]: string;
    [model.PROPS_USE_CUSTOM_IMAGERY]: boolean;
    [model.PROPS_USE_OVERVIEW_MAP]: boolean;
    [model.PROPS_USE_PRODUCT_RECS]: boolean;
    [model.PROPS_USE_IMPORT_DATE]: boolean;
    [model.PROPS_CUSTOMER_GUIDS]: string[];
    [model.PROPS_NON_FIELD_FEATURE_GUIDS]: string[];
}

export interface IWithReportProps {
    clearReportHubData: () => void;
    createReport: (request: { model: IReportData; reportType?: string }) => void;
    createReports: (request: { model: IReportData; reportType?: string }[]) => void;
    createReportSuccess: (payload: []) => void;
    crops: ICropDropdownOption[];
    deleteSelectedReports: () => void;
    fetchPicklistData: (options: { picklistKey: number }) => any;
    fetchUnitData: (options: { unitTypeKey: number }) => any;
    fileNameFormats: IFileNameFormat[];
    getFileNameFormat: (payload?: any) => any;
    getReportPref: () => any;
    getReportPreferences: () => void;
    getReportTypeList: (reportTypePayload: { url: string }) => any;
    getReportTypePageList: (reportTypePagePayload: { url: string }) => any;
    getTestingLabVendorListData: () => any;
    getTestPackageListData: (vendorGuid: string) => any;
    getYieldByAnalysisCategory: () => void;
    getYieldByCompareByOption: (payload?: any) => any;
    intl: intlShape;
    mergeReportData: (payload: {
        reportType?: string;
        reportData: Record<string, any> | null;
    }) => void;
    needs: (...args: any[]) => number;
    recGeneralGuidToRecMap: Map<string, RecSummary>;
    reportData: { [key: string]: IReportData };
    reportPreferences: Record<string, any>;
    reportTypePages: IReportTypePage[];
    reportTypes: IReportType[];
    requestIds: { [key: number]: string };
    resetReportData: () => void;
    resetReportPreferencesCache: () => void;
    seasons: PicklistAPI.IPicklistItem[];
    selectedCustomerGuids: string[];
    selectedEventGuidSet: Set<string>;
    selectedFieldCount: number;
    selectedFields: ReportAPI.IField[];
    selectedNonFieldFeatureGuids: Immutable.Set<string>;
    selectedRecGuidSet: Set<string>;
    setReportDataValue: (payload: any) => void;
    totalFieldsCount: number;
    updateYieldByAnalysisCategory: () => void;
    updateYieldByCompareByOptions: (payload?: any) => any;
    yieldByCompareByOptions: IYieldByCategory;
}

interface IWithReportState {
    createReportHubProgress: boolean;
    errorMessages: IErrorMessage[];
    selectedReportTypeNames: string[];
    showUseAllFieldsDialog: boolean;
    useAllFields: boolean;
}

export const withReport = <P extends IWithReportProps>(
    ChildComponent: React.ComponentType<P>,
    service: {
        REPORT_TYPE_LIST_URL: string;
        REPORT_TYPE_PAGE_LIST_URL: string;
    }
): ConnectedComponent<any, Omit<unknown, never>> => {
    class ReportWrapper extends Component<IWithReportProps, IWithReportState> {
        private selectedReportTypes: string[];
        private createReportRequestId: number;
        private createReportRequestIds: number[];

        constructor(props: P) {
            super(props);
            this.state = {
                createReportHubProgress: false,
                errorMessages: [],
                selectedReportTypeNames: [],
                useAllFields: false,
                showUseAllFieldsDialog: false,
            };

            this.selectedReportTypes = [];
            this.createReportRequestId = null;
            this.createReportRequestIds = [];
        }

        _initializeReportData = (): void => {
            const { REPORT_TYPE_LIST_URL, REPORT_TYPE_PAGE_LIST_URL } = service;
            const reportTypePayload = {
                url: REPORT_TYPE_LIST_URL,
            };
            const reportTypePagePayload = {
                url: REPORT_TYPE_PAGE_LIST_URL,
            };
            this.props.needs([
                this.props.getReportPref(),
                this.props.getReportTypeList(reportTypePayload),
                this.props.getReportTypePageList(reportTypePagePayload),
                this.props.fetchPicklistData({
                    picklistKey: picklistNames.getPickListCode(
                        picklistNames.PICKLIST_CROPPING_SEASON
                    ),
                }),
                this.props.fetchUnitData({
                    unitTypeKey: unitNames.getUnitCode(unitNames.UNIT_WEIGHT),
                }),
                this.props.getFileNameFormat(),
                this.props.getTestingLabVendorListData(),
                this.props.getYieldByCompareByOption(),
            ]);
        };

        _appendReportStatus = (selectedFields: ReportAPI.IField[]): IReportStatus[] => {
            return selectedFields.map((field) => {
                return {
                    ...field,
                    [model.PROPS_REPORT_STATUS]: keywords.uploading,
                };
            });
        };

        // On a successful create, the report checkboxes are cleared visually (selection
        // state is reset below), but the per-report `reportData` buckets are NOT cleared
        // here. Re-selecting a report then re-uses the lingering bucket (report-options
        // only re-clones the current season/crop/dates from "default" when the bucket is
        // empty), so a stale season would resurface. Clear each just-created report's
        // bucket so a re-select starts clean. (HW-11750 follow-up)
        _clearCreatedReportData = (): void => {
            this.state.selectedReportTypeNames.forEach((reportType) => {
                this.props.mergeReportData({ reportType, reportData: null });
            });
        };

        _proccessCreateReportRequest = (nextProps: P): void => {
            if (nextProps.requestIds && this.createReportRequestId) {
                if (nextProps.requestIds[this.createReportRequestId] === SUCCESS) {
                    this._clearCreatedReportData();
                    this.selectedReportTypes = [];
                    this.createReportRequestId = null;
                    this.setState(
                        {
                            createReportHubProgress: true,
                            selectedReportTypeNames: [],
                        },
                        () => {
                            this.props.resetReportPreferencesCache();
                            this.props.getReportPreferences();
                        }
                    );
                } else if (nextProps.requestIds[this.createReportRequestId] === FAILURE) {
                    this.setState({ createReportHubProgress: true });
                    this.createReportRequestId = null;
                }
            }
            if (this.createReportRequestIds.length > 0 && nextProps.requestIds) {
                const allSuccess = this.createReportRequestIds.every(
                    (requestId) => nextProps.requestIds[requestId] === SUCCESS
                );
                const someFailed = this.createReportRequestIds.some(
                    (requestId) => nextProps.requestIds[requestId] === FAILURE
                );
                if (allSuccess) {
                    this._clearCreatedReportData();
                    this.selectedReportTypes = [];
                    this.createReportRequestIds = [];
                    this.setState(
                        {
                            createReportHubProgress: true,
                            selectedReportTypeNames: [],
                        },
                        () => {
                            this.props.resetReportPreferencesCache();
                            this.props.getReportPreferences();
                        }
                    );
                } else if (someFailed) {
                    this.setState({ createReportHubProgress: true });
                    this.createReportRequestIds = [];
                }
            }
        };

        selectReportType = ({
            guid,
            name,
            checked,
        }: {
            guid: string;
            name: string;
            checked?: boolean;
        }): void => {
            const selectedReportTypeIndex = this.selectedReportTypes.indexOf(guid);
            const selectedReportTypeNames = [...this.state.selectedReportTypeNames];
            if (selectedReportTypeIndex > -1) {
                selectedReportTypeNames.splice(selectedReportTypeNames.indexOf(name), 1);
                this.selectedReportTypes.splice(selectedReportTypeIndex, 1);
            } else {
                selectedReportTypeNames.push(name);
                this.selectedReportTypes.push(guid);
            }
            this.props.mergeReportData({
                reportType: name,
                reportData: checked ? {} : null,
            });
            this.setState({
                selectedReportTypeNames,
            });
        };

        sortFieldsByCustomerFarmField(selectedField: ReportAPI.IField[]): ReportAPI.IField[] {
            return selectedField
                .map(({ customerName, farmName, fieldName }, index) => ({
                    index,
                    value: `${customerName}${farmName}${fieldName}`.replace(/\s/g, ""),
                }))
                .sort((a, b) =>
                    b.value.localeCompare(a.value, undefined, {
                        numeric: true,
                        sensitivity: "base",
                    })
                )
                .map((el) => selectedField[el.index]);
        }

        // A row's purpose is unlinked only when its crop resolves to a linked crop (non-null
        // `agvancePurposes`) whose purpose list doesn't contain the row's guid -- a missing
        // purpose selection or an unresolved/unlinked crop is not itself a purpose error.
        _isCropYieldGoalPurposeUnlinked = (row: ICropYieldData): boolean => {
            if (!row.cropPurposeGuid) {
                return false;
            }
            const crop = (this.props.crops ?? []).find((c) => c.guid === row.cropGuid);
            return (
                crop?.agvancePurposes != null &&
                !crop.agvancePurposes.some((purpose) => purpose.purposeGuid === row.cropPurposeGuid)
            );
        };

        _hasUnlinkedCropYieldGoal = (reportData: IReportData): boolean => {
            const cropYieldRows = [
                ...(reportData[model.PROPS_CROP_DATA] ?? []),
                ...(reportData[model.PROPS_BREAK_EVEN_CROP_DATA] ?? []),
            ];
            return cropYieldRows.some(
                (row) =>
                    isCropUnlinked(row.cropGuid, this.props.crops ?? []) ||
                    this._isCropYieldGoalPurposeUnlinked(row)
            );
        };

        _prepareReportData = (reportData: IReportData, errorMessages: IErrorMessage[]) => {
            if (
                errorMessages.find((em) => em.message === messages.dateOrderError) != null &&
                reportData[model.PROPS_START_DATE] &&
                reportData[model.PROPS_END_DATE]
            ) {
                const startDate = new Date(reportData[model.PROPS_START_DATE]);
                const endDate = new Date(reportData[model.PROPS_END_DATE]);
                if (startDate > endDate) {
                    errorMessages.push({
                        message: messages.dateOrderError,
                        messageOptions: {},
                    });
                }
            }
            if (
                errorMessages.find((em) => em.message === messages.unlinkedCropYieldGoalError) ==
                    null &&
                this._hasUnlinkedCropYieldGoal(reportData)
            ) {
                errorMessages.push({
                    message: messages.unlinkedCropYieldGoalError,
                    messageOptions: {},
                });
            }
            if (!reportData[model.PROPS_PER_REPORT_OPTION]) {
                reportData[model.PROPS_PER_REPORT_OPTION] = constants.DEFAULT_PER_REPORT_OPTION;
            }
            if (!reportData[model.PROPS_SELECTED_BASEMAP]) {
                reportData[model.PROPS_SELECTED_BASEMAP] = constants.DEFAULT_SELECTED_BASEMAP;
            }
            if (!reportData[model.PROPS_START_POSITION]) {
                reportData[model.PROPS_START_POSITION] = 1;
            }
            reportData[model.PROPS_USE_CUSTOM_IMAGERY] =
                reportData[model.PROPS_USE_CUSTOM_IMAGERY] || false;
        };

        _validateReportData = (selectedReportTypeNames, errorMessages, allReportData): boolean => {
            if (this.props.selectedFields.length === 0 && !this.state.useAllFields) {
                if (
                    selectedReportTypeNames.length === 1 &&
                    [
                        ReportAPI.ReportNames.SOIL_TESTING_REQUIRED_REPORT,
                        ReportAPI.ReportNames.AGVANCE_SKY_FIELD_REFERENCE_LIST,
                    ].includes(selectedReportTypeNames[0])
                ) {
                    this.setState({ showUseAllFieldsDialog: true });
                    return false;
                } else {
                    errorMessages.push({
                        message: messages.selectFieldError,
                        messageOptions: {},
                    });
                }
            }
            if (this.selectedReportTypes.length === 0) {
                errorMessages.push({
                    message: messages.selectReportTypeError,
                    messageOptions: {},
                });
            }
            if (
                this.props.recGeneralGuidToRecMap?.size > 0 &&
                this.props.selectedRecGuidSet?.size > 0 &&
                selectedReportTypeNames.includes(keywords.loadsheet)
            ) {
                const customerFarmFieldInfo: string[] = [];
                const isRecProcessing = Array.from(this.props.selectedRecGuidSet).some(
                    (recGeneralGuid) => {
                        const recSummary = this.props.recGeneralGuidToRecMap.get(recGeneralGuid);
                        const isProcessing = loadsheetPreventionStatusCodes.includes(
                            recSummary.importedStatus
                        );
                        if (isProcessing) {
                            const recField = this.props.selectedFields.find(
                                (f) => f.fieldGuid === recSummary.fieldGuid
                            );
                            customerFarmFieldInfo.push(
                                [recField.customerName, recField.farmName, recField.fieldName]
                                    .filter((x) => x?.trim().length > 0)
                                    .join(" / ")
                            );
                        }
                        return isProcessing;
                    }
                );
                if (isRecProcessing) {
                    errorMessages.push({
                        message: errorCodeMessages[2865],
                        messageOptions: {
                            codeValue: `\n ${customerFarmFieldInfo.join("\n")}`,
                        },
                    });
                }
            }
            selectedReportTypeNames.forEach((reportType) => {
                const reportData = allReportData[reportType];
                this._prepareReportData(reportData, errorMessages);
            });
            this.setState({
                errorMessages,
                createReportHubProgress: false,
            });

            return errorMessages.length === 0;
        };

        createReport = (): void => {
            this.props.clearReportHubData();
            const errorMessages: IErrorMessage[] = [];
            const { selectedReportTypeNames } = this.state;
            const allReportData = JSON.parse(JSON.stringify(this.props.reportData));

            if (this._validateReportData(selectedReportTypeNames, errorMessages, allReportData)) {
                this._sendCreateReportRequests(selectedReportTypeNames, allReportData);
            }
        };

        _sendCreateReportRequests = (selectedReportTypeNames, allReportData) => {
            const batchGuid = uuid();
            const createReportData = [];
            selectedReportTypeNames.forEach((reportType, index) => {
                if (NEW_REPORTS.includes(selectedReportTypeNames[index])) {
                    // New reports already have everything except the field list in the report data
                    let reportData = this.props.reportData[selectedReportTypeNames[index]];
                    const reportComponent = reportOptionsComponents[selectedReportTypeNames[index]];
                    if (reportComponent?.prepareRequest) {
                        reportData = reportComponent.prepareRequest(reportData, {
                            selectedFields: this.props.selectedFields,
                        }) as IReportData;
                    }
                    reportData[model.PROPS_BATCH_GUID] = batchGuid;
                    reportData[model.PROPS_FIELD_GUIDS] = this.props.selectedFields.map(
                        (f) => f.fieldGuid
                    );
                    createReportData.push({
                        model: reportData,
                        reportType: selectedReportTypeNames[index],
                    });
                    return;
                }
                const reportData = allReportData[reportType];
                if (reportData[model.PROPS_CROPPING_SEASON_GUID]) {
                    reportData[model.PROPS_CROPPING_SEASON_GUID_LIST] = [
                        reportData[model.PROPS_CROPPING_SEASON_GUID],
                    ];
                }
                reportData[model.PROPS_BATCH_GUID] = batchGuid;
                if (reportData[model.PROPS_REPORT_IMAGE_CONFIG]) {
                    reportData[model.PROPS_REPORT_IMAGE_CONFIG] = JSON.stringify(
                        reportData[model.PROPS_REPORT_IMAGE_CONFIG]
                    );
                }
                if (reportData[model.PROPS_REPORT_TYPE_PAGE_SELECTION]) {
                    reportData[model.PROPS_REPORT_TYPE_PAGE_SELECTION] = JSON.stringify(
                        reportData[model.PROPS_REPORT_TYPE_PAGE_SELECTION]
                    );
                }
                reportData[model.PROPS_SELECTED_REC_GUID_LIST] = Array.from(
                    this.props.selectedRecGuidSet
                );
                reportData[model.PROPS_SELECTED_EVENT_GUID_LIST] = Array.from(
                    this.props.selectedEventGuidSet
                );
                reportData[model.PROPS_FIELD_ITEM_LIST] = this._appendReportStatus(
                    this.sortFieldsByCustomerFarmField(this.props.selectedFields)
                );
                reportData[model.PROPS_REPORT_TYPE_GUID_LIST] = [this.selectedReportTypes[index]];
                reportData[model.PROPS_ALL_CUSTOMERS_FIELDS_YN] = this.state.useAllFields;
                reportData[model.PROPS_CUSTOMER_GUIDS] = Array.from(
                    this.props.selectedCustomerGuids
                );
                reportData[model.PROPS_NON_FIELD_FEATURE_GUIDS] = Array.from(
                    this.props.selectedNonFieldFeatureGuids
                );
                createReportData.push({
                    model: reportData,
                });
            });

            const createReportRequestId = this.props.needs([
                this.props.createReports(createReportData),
            ]);
            this.createReportRequestIds.push(createReportRequestId);
        };

        onDialogBoxClose = (): void => {
            this.setState({
                errorMessages: [],
            });
        };
        onUseAllFieldsDialogClose = (): void => {
            this.setState({ useAllFields: false, showUseAllFieldsDialog: false });
        };
        onUseAllFieldsDialogConfirm = (): void => {
            this.setState({ useAllFields: true, showUseAllFieldsDialog: false }, this.createReport);
        };

        onInputChange = (key: string, value: any, reportType: string): void => {
            this.props.setReportDataValue({ key, value, reportType });
        };

        onAllReportCreated = (): void => {
            this.setState({ createReportHubProgress: false });
        };

        componentDidMount(): void {
            this._initializeReportData();
        }

        componentWillUnmount(): void {
            this.props.resetReportData();
            this.props.resetReportPreferencesCache();
        }

        UNSAFE_componentWillReceiveProps(nextProps: P): void {
            this._proccessCreateReportRequest(nextProps);
        }

        render(): JSX.Element {
            const { createReportHubProgress, selectedReportTypeNames } = this.state;
            const {
                fileNameFormats,
                seasons,
                reportPreferences,
                reportTypes,
                reportTypePages,
                selectedFieldCount,
            } = this.props;
            const props = {
                createReport: this.createReport,
                createReportHubProgress,
                fileNameFormats,
                selectedFieldCount,
                getTestPackageListData: this.props.getTestPackageListData,
                getYieldByAnalysisCategory: this.props.getYieldByAnalysisCategory,
                getYieldByCompareByOption: this.props.getYieldByCompareByOption,
                mergeReportData: this.props.mergeReportData,
                initialRequestOptions: {
                    [model.PROPS_FIELD_GUID_LIST]: getFieldGuidArray(this.props.selectedFields),
                },
                needs: this.props.needs,
                onAllReportCreated: this.onAllReportCreated,
                onInputChange: this.onInputChange,
                reportData: this.props.reportData,
                reportPreferences,
                reportTypes,
                reportTypePages,
                requestIds: this.props.requestIds,
                seasons,
                selectReportType: this.selectReportType,
                selectedEventGuidSet: this.props.selectedEventGuidSet,
                selectedFields: this.props.selectedFields,
                selectedRecGuidSet: this.props.selectedRecGuidSet,
                selectedReportTypeNames,
                selectedReportTypes: this.selectedReportTypes,
                updateYieldByAnalysisCategory: this.props.updateYieldByAnalysisCategory,
                updateYieldByCompareByOptions: this.props.updateYieldByCompareByOptions,
                yieldByCompareByOptions: this.props.yieldByCompareByOptions,
            };

            const { errorMessages } = this.state;
            const { formatMessage, formatNumber } = this.props.intl;

            return (
                <div className="with-report-container">
                    <ChildComponent {...(props as unknown as P)} />
                    <DialogBox
                        footerType={DialogBoxFooterType.NONE}
                        isOpen={errorMessages.length > 0}
                        onClose={() => this.onDialogBoxClose()}
                        title={formatMessage(messages.required)}
                        testId="with-report-required-modal"
                    >
                        <div>
                            {errorMessages.map((error, index) => (
                                <p className="error-message" key={index}>
                                    {formatMessage(error.message, error.messageOptions)}
                                </p>
                            ))}
                        </div>
                    </DialogBox>
                    <DialogBox
                        isOpen={this.state.showUseAllFieldsDialog}
                        isModal={true}
                        title={formatMessage(messages.confirmTitle)}
                        draggable={true}
                        hideCloseX={true}
                        onAction={() => this.onUseAllFieldsDialogConfirm()}
                        onClose={() => this.onUseAllFieldsDialogClose()}
                        footerType={DialogBoxFooterType.YES_NO}
                        testId="with-report-use-all-fields-modal"
                    >
                        {formatMessage(messages.useAllFields, {
                            numFields: formatNumber(this.props.totalFieldsCount),
                        })}
                    </DialogBox>
                </div>
            );
        }
    }

    // Supplies `crops` via a hook since ReportWrapper is a class: the Agvance-linkage lookup used
    // by _hasUnlinkedCropYieldGoal must be fetched here and passed down. RTK Query dedupes
    // identical-args queries, so this doesn't add a second call alongside
    // report-options-container.tsx's own useCropList().
    const ReportWrapperWithCropLinkage: React.FC<IWithReportProps> = (props) => {
        const { crops: rawCrops } = useCropList({ scope: useReportCropScope() });
        return <ReportWrapper {...props} crops={toCropDropdownOptions(rawCrops)} />;
    };

    return withMasked(
        connect(
            mapStateToProps,
            mapDispatchToProps
        )(injectIntl(WithReportErrors(ReportWrapperWithCropLinkage)))
    );
};

const mapStateToProps = (state): Partial<IWithReportProps> => ({
    reportData: selectors.getReportData(state),
    reportTypes: selectors.getReportTypes(state),
    reportTypePages: selectors.getReportTypePages(state),
    fileNameFormats: selectors.getFileNameFormatData(state),
    seasons: picklistSelectors.getPicklistOptionsFromCode(
        state,
        picklistNames.getPickListCode(picklistNames.PICKLIST_CROPPING_SEASON)
    ),
    selectedFields: selectors.getFields(state) ?? [],
    selectedFieldCount: cdSelectors.getSelectedFieldsCount(state),
    reportPreferences: selectors.getReportPref(state),
    selectedEventGuidSet: getSelectedEventGuidSet(state),
    selectedRecGuidSet: getSelectedRecGuidSet(state),
    recGeneralGuidToRecMap: recsSelectors.getRecGeneralGuidToRecSummaryMap(state),
    totalFieldsCount: cdSelectors.getTotalFieldsCount(state),
    selectedCustomerGuids: getSelectedCustomerGuids(state),
    selectedNonFieldFeatureGuids: getSelectedNonFieldFeaturesGuids(state),
    yieldByCompareByOptions: selectors.getYieldByCompareByOptions(state),
});

const mapDispatchToProps = (dispatch): Partial<IWithReportProps> => ({
    clearReportHubData: () => dispatch(actions.clearReportHubData()),
    // Actions to be dispatched using needs
    createReport: (payload) => actions.createReport(payload),
    createReports: (payload) => actions.createReports(payload),
    fetchPicklistData: (payload) => picklistActions.fetchPicklistData(payload),
    fetchUnitData: (payload) => unitActions.fetchUnitData(payload),
    getFileNameFormat: (payload) => actions.getFileNameFormat(payload),
    getReportPref: () => actions.getReportPrefData(),
    getReportPreferences: () => dispatch(actions.getReportPrefData()),
    getReportTypeList: (payload) => actions.getReportTypeList(payload),
    getReportTypePageList: (payload) => actions.getReportTypePageList(payload),
    getYieldByCompareByOption: (payload) => actions.getYieldByCompareByOption(payload),
    mergeReportData: (payload) => dispatch(actions.mergeReportData(payload)),
    resetReportData: () => dispatch(actions.resetReportData()),
    resetReportPreferencesCache: () => dispatch(Ai360API.util.invalidateTags(["LoginPreferences"])),
    setReportDataValue: (payload) => dispatch(actions.setReportDataValue(payload)),
    updateYieldByCompareByOptions: (payload) => actions.updateYieldByCompareByOptions(payload),
    getTestPackageListData: (vendorGuid) => vendorActions.getTestPackageListData(vendorGuid),
    getTestingLabVendorListData: () => vendorActions.getTestingLabVendorListData(),
});
